import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Users, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { carpoolsAPI, scheduleAPI, usersAPI } from '../services/api';
import {
  PageLoading,
  PageHead,
  PageStats,
  PageButton,
  PagePanel,
  PageEmpty,
  PageListCard,
  UpcomingRideRow,
  ActivityIconBadge,
} from '../components/appPage';
import './Dashboard.css';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [carpools, setCarpools] = useState([]);
  const [upcomingRides, setUpcomingRides] = useState([]);
  const [childCount, setChildCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [carpoolsRes, ridesRes, childrenRes] = await Promise.all([
        carpoolsAPI.getCarpools(),
        scheduleAPI.getMyRides(),
        usersAPI.getChildren().catch(() => ({ data: { children: [] } })),
      ]);
      setCarpools(carpoolsRes.data.carpools || []);
      setUpcomingRides(ridesRes.data.rides || []);
      setChildCount((childrenRes.data.children || []).length);
    } catch (e) {
      console.error('Dashboard load failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return t('dashboard.greetingMorning', 'Добро утро');
    if (h < 18) return t('dashboard.greetingDay', 'Добър ден');
    return t('dashboard.greetingEvening', 'Добър вечер');
  };

  if (loading) {
    return <PageLoading message={t('common.loading')} />;
  }

  const title = (
    <>
      {greeting()}
      {user?.firstName ? `, ${user.firstName}` : ''}
    </>
  );

  return (
    <div className="dashboard-page page-shell">
      <PageHead title={title} lead={t('dashboard.subtitle')} />

      <PageStats
        ariaLabel={t('dashboard.title')}
        items={[
          { value: carpools.length, label: t('dashboard.statCarpools', 'Карпули') },
          { value: upcomingRides.length, label: t('dashboard.statRides', 'Предстоящи пътувания') },
          { value: childCount, label: t('dashboard.statChildren', 'Деца в профила') },
        ]}
      />

      <nav className="page-actions" aria-label="Бързи действия">
        <PageButton to="/carpools/new" variant="primary">
          <Plus size={18} strokeWidth={2} aria-hidden />
          {t('carpools.create', 'Нов карпул')}
        </PageButton>
        <PageButton to="/children" variant="ghost">
          <Users size={18} strokeWidth={2} aria-hidden />
          {t('children.title', 'Деца')}
        </PageButton>
        <PageButton to="/invitations" variant="ghost">
          <Mail size={18} strokeWidth={2} aria-hidden />
          {t('invitations.title', 'Покани')}
        </PageButton>
      </nav>

      <div className="dashboard-grid">
        <PagePanel
          title={t('dashboard.myCarpools', 'Моите карпули')}
          titleId="dash-carpools-heading"
          headerAction={
            <Link to="/carpools" className="page-link">
              {t('common.viewAll', 'Виж всички')}
            </Link>
          }
        >
          {carpools.length === 0 ? (
            <PageEmpty
              text={t('dashboard.noCarpools')}
              action={
                <Link to="/carpools/new" className="btn-primary">
                  {t('dashboard.createFirst')}
                </Link>
              }
            />
          ) : (
            <ul className="page-list">
              {carpools.slice(0, 6).map((c) => (
                <li key={c.id}>
                  <PageListCard
                    to={`/carpools/${c.id}`}
                    icon={<ActivityIconBadge activity={c.activity} size={22} />}
                    title={c.name}
                    meta={`${c.schedule?.time || '—'} · ${c.members?.length || 0} ${t(
                      'carpools.membersCountSuffix',
                      'членове'
                    )}`}
                    trailing={<span className="page-pill">{t('common.active', 'Активен')}</span>}
                  />
                </li>
              ))}
            </ul>
          )}
        </PagePanel>

        <PagePanel
          title={t('dashboard.upcomingRides') || 'Предстоящи пътувания'}
          titleId="dash-rides-heading"
        >
          {upcomingRides.length === 0 ? (
            <PageEmpty compact text={t('dashboard.noUpcomingRides')} />
          ) : (
            <ul className="upcoming-rides">
              {upcomingRides.slice(0, 6).map((ride) => (
                <UpcomingRideRow key={ride.id} ride={ride} />
              ))}
            </ul>
          )}
        </PagePanel>
      </div>
    </div>
  );
};

export default Dashboard;
